import { createHash } from "node:crypto";
import type { LocalProjectControlProjectDiscovery } from "@wakeloop/schemas";

type DiscoveryEpochToken = {
  clientGeneration: number;
  organizationId: string;
  sequence: number;
};

type CurrentDiscoveryEpoch = DiscoveryEpochToken & {
  candidateTokens: Set<string>;
};

export class LocalProjectDiscoveryEpochs {
  private readonly currentByOrganization = new Map<
    string,
    CurrentDiscoveryEpoch
  >();
  private nextSequence = 1;

  begin(input: {
    clientGeneration: number;
    organizationId: string;
  }): DiscoveryEpochToken {
    const token = {
      ...input,
      sequence: this.nextSequence,
    };
    this.nextSequence += 1;
    const current = this.currentByOrganization.get(input.organizationId);
    if (!current || input.clientGeneration >= current.clientGeneration) {
      this.currentByOrganization.set(input.organizationId, {
        ...token,
        candidateTokens: new Set(),
      });
    }
    return token;
  }

  complete(
    token: DiscoveryEpochToken,
    projects: readonly LocalProjectControlProjectDiscovery[]
  ): void {
    const current = this.currentByOrganization.get(token.organizationId);
    if (!current || current.sequence !== token.sequence) {
      return;
    }
    const candidateTokens = new Set<string>();
    for (const project of projects) {
      for (const candidate of project.candidates) {
        candidateTokens.add(
          buildCandidateToken({
            candidateId: candidate.candidateId,
            candidatePath: candidate.canonicalPath,
            projectId: project.projectId,
          })
        );
      }
    }
    this.currentByOrganization.set(token.organizationId, {
      ...token,
      candidateTokens,
    });
  }

  hasCandidate(input: {
    candidateId: string;
    candidatePath: string;
    clientGeneration: number;
    organizationId: string;
    projectId: string;
  }): boolean {
    const current = this.currentByOrganization.get(input.organizationId);
    return Boolean(
      current &&
        current.clientGeneration === input.clientGeneration &&
        current.candidateTokens.has(buildCandidateToken(input))
    );
  }
}

function buildCandidateToken(input: {
  candidateId: string;
  candidatePath: string;
  projectId: string;
}): string {
  const identity = {
    candidateId: input.candidateId,
    candidatePath: input.candidatePath,
    projectId: input.projectId,
  };
  return createHash("sha256").update(JSON.stringify(identity)).digest("hex");
}
